import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useOpenFin } from '../../../helpers/hooks/useOpenFin'
import { addLogItem } from '../../../store/log/actions'
import { cancelOrder, submitOrder } from '../../../store/order/actions'
import { getAggressErrors } from '../../../store/order/selectors'
import { OrderType } from '../../../store/order/types'
import { getSpreadEligibility } from '../../../store/rfq/helpers'
import { getSecurityStaticDataById } from '../../../store/securities/selectors'
import { useAppDispatch, useAppSelector } from '../../../store/types'
import {
  getMaxTradeSize,
  getMaxTradeSizeIsHardLimit
} from '../../../store/userPreferences/selectors'
import { removeAggressorWindow } from '../../../store/windows/actions'
import { AggressorWindowInfo } from '../../../store/windows/types'
import AggressorModalFooter from '../AggressorModalFooter/AggressorModalFooter'
import HeaderModal from '../ModalHeader/ModalHeader'
import ModalNotification from '../ModalNotification/ModalNotification'
import ModalSecurityInformationWrapper from '../ModalSecurityInformationWrapper'
import AggressorModalContent from './AggressorModalContent'
import {
  useAggressorModalOrders,
  useAggressorOrderForAggressorWindow,
  useSecurityModalInfo,
  useValueChangeAlert
} from './helpers'

import styles from '../modal.module.scss'

export interface Props {
  windowInfo: AggressorWindowInfo
  index: number
  isActiveWindow?: boolean
  handleMinimize: (index: number) => void
}

const alertMsgAmountLessQuantity = 'Amount is less than minimum quantity'
const alertMsgAmountMoreQuantity = 'Amount is more than maximum quantity'
const alertMsgAmountMustBeAnInteger = 'Amount must be an integer'
const alertMsgPriceChange = 'Price has changed - check price'
const alertMsgSizeChange = 'Size has changed - check size'
const notificationModalType = 'alert'
const submitLockoutMs = 2000

const AggressorModal: FC<Props> = ({
  windowInfo,
  isActiveWindow = true,
  handleMinimize,
  index
}) => {
  const dispatch = useAppDispatch()
  const { isOpenFin, finWindowAction } = useOpenFin()
  const { initialOrder } = useAggressorModalOrders(windowInfo)
  const errorMsg = useAppSelector(getAggressErrors)(windowInfo.transactionId)
  const maxTradeSize = useAppSelector(getMaxTradeSize)
  const maxTradeSizeIsHardLimit = useAppSelector(getMaxTradeSizeIsHardLimit)

  const aggressorOrder = useAggressorOrderForAggressorWindow(
    windowInfo.transactionId
  )
  const [size, setSize] = useState(0)
  const securityModalInfo = useSecurityModalInfo(
    windowInfo.initialOrderId,
    size
  )
  const [selectedAccount, setSelectedAccount] = useState(
    securityModalInfo?.accountIdToName[securityModalInfo.defaultAccountId]
  )
  const security = useAppSelector(getSecurityStaticDataById)(
    windowInfo.securityId
  )
  const [tradeAmt, setTradeAmt] = useState('')
  const isCancelAttempt = aggressorOrder?.status === 'cancelled'
  const [initialAmount, setInitialAmount] = useState(0)
  const [buySellAmount, setBuySellAmount] = useState(0)
  const [spotCrossSelection, setSpotCrossSelection] = useState('')
  /*
    The ref, not the disabled attribute, is what makes a double click safe —
    React may not have committed the disable before the second click lands.
  */
  const hasSubmitted = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const minSizeForTsyHedge = securityModalInfo?.minSizeForTsyHedge
    ? securityModalInfo?.minSizeForTsyHedge
    : '500'

  useEffect(() => {
    if (securityModalInfo && security) {
      securityModalInfo.spotHedgePreference.startsWith('H') &&
      securityModalInfo.benchmarkPrice &&
      Number(tradeAmt) >= Number(minSizeForTsyHedge) &&
      getSpreadEligibility(security.sAndPRating, securityModalInfo.spread)
        ? setSpotCrossSelection('H')
        : setSpotCrossSelection('S')
    }
  }, [securityModalInfo?.spotHedgePreference, tradeAmt, security])

  // ------------------ modal alerts ------------------ //
  const showAlerts =
    aggressorOrder?.status === 'creationPending' ||
    aggressorOrder?.status === 'cancelled'

  const shouldAlertPriceChange = useValueChangeAlert(
    aggressorOrder?.status,
    ['waitingForConfirmation', 'accepted'],
    securityModalInfo?.price,
    buySellAmount
  )

  const shouldAlertSizeChange = useValueChangeAlert(
    undefined,
    [],
    securityModalInfo?.maximumSize,
    buySellAmount
  )

  const takerAlerts = useMemo(() => {
    const amountQty = Number(tradeAmt)
    if (!Number.isInteger(amountQty)) {
      return alertMsgAmountMustBeAnInteger
    }
    if (amountQty > (securityModalInfo?.maximumSize ?? 0)) {
      return alertMsgAmountMoreQuantity
    }
    if (amountQty !== 0 && amountQty < (securityModalInfo?.minimumSize ?? 0)) {
      return alertMsgAmountLessQuantity
    }
    return ''
  }, [tradeAmt, securityModalInfo?.maximumSize, securityModalInfo?.minimumSize])

  const isAmountInputNOK = useMemo(() => {
    if (aggressorOrder?.status === 'cancelled' && securityModalInfo) {
      const amount = Number(tradeAmt)
      return (
        initialOrder === undefined ||
        isNaN(amount) ||
        !Number.isInteger(amount) ||
        amount < securityModalInfo.minimumSize
      )
    }
    if (takerAlerts || tradeAmt === '') {
      return true
    }
    return false
  }, [
    aggressorOrder?.status,
    initialOrder,
    tradeAmt,
    securityModalInfo,
    takerAlerts
  ])

  useEffect(() => {
    const newSize = Number(tradeAmt)
    if (!Number.isNaN(newSize) && newSize !== size) {
      setSize(newSize)
    }
  }, [tradeAmt, size])

  const [type] = useState<OrderType>(
    initialOrder?.type === 'buy' ? 'sell' : 'buy'
  )

  const isOrderPending =
    aggressorOrder?.status === 'creationPending' ||
    aggressorOrder?.status === 'pending' ||
    aggressorOrder?.status === 'cancelled'

  const liquidityRemoved = !initialOrder && isOrderPending

  useEffect(() => {
    if (securityModalInfo && !selectedAccount) {
      setSelectedAccount(
        securityModalInfo.accountIdToName[securityModalInfo.defaultAccountId]
      )
    }
  }, [securityModalInfo, selectedAccount])

  const handleCloseModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setTradeAmt('')
    dispatch(removeAggressorWindow(windowInfo))
    if (isOpenFin) {
      void finWindowAction(
        `Aggressor/${windowInfo.initialOrderId}/${security?.id}`,
        'close'
      )
    }
  }

  const handleCancelAttempt = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (aggressorOrder) {
      dispatch(cancelOrder(aggressorOrder.id))
      dispatch(
        addLogItem(
          'handleCancelAttempt for order ' +
            aggressorOrder.id +
            (security
              ? ' on security ' + security.id + ' / ' + security.cusip
              : '')
        )
      )
    } else {
      console.error('orderId not found')
    }
  }

  const onChangeTradeAmt = (newValue: string) => {
    setTradeAmt(newValue)
  }

  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handleSellOrBuySubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (hasSubmitted.current) {
      return
    }
    if (initialOrder?.id) {
      hasSubmitted.current = true
      setIsSubmitting(true)
      let custId = 0
      if (securityModalInfo) {
        const strArr = Object.keys(securityModalInfo.accountIdToName)
        const numArr = strArr.map((n) => Number(n))
        custId =
          numArr.find(
            (key) => securityModalInfo.accountIdToName[key] === selectedAccount
          ) ?? 0
      }
      dispatch(
        submitOrder(
          initialOrder,
          initialOrder.id,
          Number(tradeAmt),
          windowInfo.transactionId,
          custId,
          spotCrossSelection,
          windowInfo.listId
        )
      )
      dispatch(
        addLogItem(
          'handleSellOrBuySubmit (aggress) aggressing ' +
            initialOrder.id +
            ', securityId: ' +
            initialOrder.securityId +
            ', price: ' +
            initialOrder.price +
            ', size: ' +
            tradeAmt +
            ', side: ' +
            (initialOrder.type === 'buy' ? 'sell' : 'buy') +
            windowInfo.listId
            ? `listId: ${windowInfo.listId}`
            : ''
        )
      )
      setBuySellAmount(Number(tradeAmt))
      setInitialAmount(initialOrder.size)
    }
  }

  useEffect(() => {
    // allows aggressor values to keep updating if there's an error
    errorMsg && setBuySellAmount(0)
  }, [errorMsg])

  // the only states where the footer offers a resubmit
  const canResubmit =
    !!errorMsg ||
    aggressorOrder?.status === 'error' ||
    aggressorOrder?.status === 'cancelled'

  useEffect(() => {
    if (canResubmit) {
      hasSubmitted.current = false
      setIsSubmitting(false)
    }
  }, [canResubmit, errorMsg, aggressorOrder?.status])

  /*
    A resubmit that fails with the same error as the last one changes no value
    we can watch, so time that case out too. Only that case — while a first
    attempt is still outstanding the lock has to hold however long it takes.
  */
  useEffect(() => {
    if (!isSubmitting || !canResubmit) return
    const timeout = setTimeout(() => {
      hasSubmitted.current = false
      setIsSubmitting(false)
    }, submitLockoutMs)
    return () => clearTimeout(timeout)
  }, [isSubmitting, canResubmit])

  if (!securityModalInfo || !security || !initialOrder) {
    return null
  }

  const borderColor = `var(--${type}SecondaryColor)`

  const expandedOrCollapsed = isActiveWindow
    ? styles.expandableModal
    : styles.collapsedModal

  const availableAmount = initialAmount > 0 ? initialAmount : initialOrder.size
  const showMinimize = !isOpenFin

  // TODO: cancelled logic should only apply
  // before aggressor order is created
  // const statusForFooter =
  //   securityModalInfo?.orderStatus === 'Cancelled'
  //     ? 'passiveOrderCancelled'
  //     : aggressorOrder?.status

  return (
    <div className={styles.modalWrapper}>
      <form onSubmit={handleSellOrBuySubmit} onKeyDown={handleEnter}>
        <section className={styles.modalMain} style={{ borderColor }}>
          <HeaderModal
            type={type}
            security={security}
            showSecurityInfo={!!securityModalInfo}
            issue={securityModalInfo?.issue}
            showMinimize={showMinimize}
            isActiveWindow={isActiveWindow}
            index={index}
            handleMinimize={handleMinimize}
            orderStatusComp={aggressorOrder?.status}
          />

          {showAlerts && (
            <>
              {!!takerAlerts && (
                <ModalNotification
                  colorStyle={notificationModalType}
                  messageNotification={takerAlerts}
                />
              )}
              {shouldAlertPriceChange && (
                <ModalNotification
                  colorStyle={notificationModalType}
                  messageNotification={alertMsgPriceChange}
                />
              )}
              {shouldAlertSizeChange && (
                <ModalNotification
                  colorStyle={notificationModalType}
                  messageNotification={alertMsgSizeChange}
                />
              )}
              {/* {(securityModalInfo.orderStatus === 'Cancelled' ||
                aggressorOrder?.status === 'cancelled') && (
                <ModalNotification
                  colorStyle={notificationModalType}
                  messageNotification="The order you're aggressing against has been canceled"
                />
              )} */}
            </>
          )}
          <div className={`${styles.contentExpandable} ${expandedOrCollapsed}`}>
            <AggressorModalContent
              securityModalInfo={securityModalInfo}
              onChangeTradeAmt={onChangeTradeAmt}
              spotCrossSelection={spotCrossSelection}
              setSpotCrossSelection={setSpotCrossSelection}
              styles={styles}
              tradeAmt={tradeAmt}
              type={type}
              orderStatusComp={aggressorOrder?.status}
              shouldAlertPriceChange={shouldAlertPriceChange}
              shouldAlertSizeChange={shouldAlertSizeChange}
              selectedAccount={selectedAccount ?? ''}
              setSelectedAccount={setSelectedAccount}
              currency={security.currency}
              buySellAmount={buySellAmount}
              isTreasury={security.product === 'PrinUSGovtOutright'}
              rating={security.sAndPRating}
            />
            <ModalSecurityInformationWrapper
              securityModalInfo={securityModalInfo}
              security={security}
            />

            {aggressorOrder && (
              <AggressorModalFooter
                order={aggressorOrder}
                orderStatusComp={aggressorOrder?.status}
                tradeAmount={Number(tradeAmt)}
                availableAmount={availableAmount}
                type={type}
                handleCancelAttempt={handleCancelAttempt}
                isCancelAttempt={isCancelAttempt}
                actionCloseModal={handleCloseModal}
                isAmountInputNOK={isAmountInputNOK}
                isSubmitting={isSubmitting}
                liquidityRemoved={liquidityRemoved}
                error={errorMsg}
                maxTradeSize={maxTradeSize}
                maxTradeSizeIsHardLimit={maxTradeSizeIsHardLimit}
                benchmarkName={security.benchmarkName}
                spotCrossSelection={spotCrossSelection}
              />
            )}
          </div>
        </section>
      </form>
    </div>
  )
}

export default AggressorModal
